import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import TeamService from '../../sdk/services/teamService';
import { getContent, getLatestArticle } from '../../sdk/services/contentService';
import ParticipantService from '../../sdk/services/participantService';
import { formatDateDMYYYY, LoggerService, PAGES, trackPage } from '../../ui/utils';
import NewsCard from '../../ui/components/NewsCard/NewsCard';
import ProgressStep from '../../ui/components/ProgressStep/ProgressStep';
import { Button, ButtonSize, ButtonStyles, ButtonTypes, ButtonWidth, useBreakpoint, Theme } from '../../ui/components';
import ArticleCarousel from '../../ui/components/ArticleCarousel/ArticleCarousel';
import WelcomeBanner from '../../ui/components/WelcomeBanner/WelcomeBanner';
import { DynamicObjectInterface } from '../../sdk/type';
import { useAPICall } from '../../hooks';
import { REACT_APP_AMWAYNOW } from '../../envVars';
import Error from '../../assets/image/icons/ico_error.svg';
import { DashboardProps } from '../dashboard/dashboardConfig';
import styles from './Homepage.module.scss';

export interface TeamData {
  totalRecords: number;
  participants: DynamicObjectInterface;
  teamCreatedDate: string;
  teamStartDate: Date;
  teamId: string | number;
  enrollmentType: string;
  weightIn: {
    weightInDate: string | null;
    height: number;
    acknowledgeBy: string | null;
    acknowledgeDateTime: string | null;
    remarks: string | null;
  };
  weightOut: {
    weightOutDate: string | null;
    height: number;
    acknowledgeBy: string | null;
    acknowledgeDateTime: string | null;
    remarks: string | null;
  };
}

interface HomepageContentType {
  banner_image: {
    url: string;
  };
  banner_image_mobile: {
    url: string;
  };
  news_announcements: {
    title: string;
    description: string;
  }[];
  amway_now_logo: {
    url?: string;
  };
}

export const Homepage = (props: DashboardProps) => {
  const { decodedData, campaignData, index } = props;

  const apiCall = useAPICall();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm'].indexOf(viewPortType) === -1;
  const [data, setData] = useState<TeamData[]>([]);
  const [content, setContent] = useState<HomepageContentType>();
  const [article, setArticle] = useState([]);
  const [loadingArticle, setLoadingArticle] = useState(true);
  const [showTeamLeaderReminder, setShowTeamLeaderReminder] = useState(true);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(true);

  const cacheKey = 'homepageContent';
  const articleCacheKey = 'latestArticles';

  useEffect(() => {
    const fetchPreLoginContent = async () => {
      try {
        const data = await getContent('introduction_page', cacheKey, true);
        setContent(data);

        const numberOfArticleShow = data?.latest_articles?.article_count ?? 9;
        const categoryUid = data?.latest_articles?.article_category[0]?.uid;
        const subCategoryUid = data?.latest_articles?.article_subcategory[0]?.uid;

        let article = [] ;

        if ( categoryUid || subCategoryUid ){
           article = await getLatestArticle(articleCacheKey, true, numberOfArticleShow, categoryUid, subCategoryUid);

          article = article.map((item: DynamicObjectInterface) => {
            return {
              imageSrc: item?.thumbnail?.thumbnail_image?.url,
              label: item?.article_category_reference[0]?.title,
              categoryColor: item?.article_category_reference[0]?.category_s_color,
              title: item?.display_title,
              date: item.article_date ? moment(item?.article_date).format('DD/MM/YYYY') : null,
              redirectUrl: REACT_APP_AMWAYNOW + item?.url,
            };
          });
        }

        setArticle(article);
        setLoadingArticle(false);
      } catch (error) {
        LoggerService.log('Failed to retrieve content data. Using default content.');
      }
    };

    const _firstTimeLogin = localStorage.getItem('isFirstTimeLogin');

    if (_firstTimeLogin === 'true') {
      setShowWelcomeBanner(true);
      localStorage.removeItem('isFirstTimeLogin');
    } else {
      setShowWelcomeBanner(false);
    }

    fetchPreLoginContent();

    const timeout = setTimeout(() => {
      trackPage(PAGES.dashboard);
    }, 1000);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    (async () => {
      if (campaignData && decodedData && index !== undefined) {
        const result = await apiCall(ParticipantService.getTeamListByUser, {
          campaignId: campaignData?.[index]?.campaignId,
          loginId: decodedData?.loginId,
          userId: decodedData?.userId,
          coachId:
            props.callFrom !== 'coachList'
              ? props?.campaignData?.[index]?.enrollment?.find(
                  (i: DynamicObjectInterface) => i.enrollmentType === 'COACH',
                )?.coachId
              : props.coachId,
          enrollmentStartDate: props.enrollmentStartDate,
          enrollmentEndDate: props.enrollmentEndDate,
          maxLength: 7,
        });

        const dataWithDetails = await Promise.all(
          result?.data?.data?.map(async (team: DynamicObjectInterface) => {
            const teamDetails = await apiCall(TeamService.getTeamDetails, `${team.teamId}`);
            const isGroupAchieved = teamDetails?.data?.data?.isGroupAchieved;

            const totalPoints = team?.participants
              ?.filter((participant: DynamicObjectInterface) => participant.status === 'ACTIVE')
              ?.map((participant: DynamicObjectInterface) => participant.point)
              ?.flatMap((item: DynamicObjectInterface) => Object.values(item))
              ?.reduce((acc: number, value: number) => acc + value, 0)
              ?.toFixed(2);

            const teamSize = team?.participationType === 'ind' ? '1' : team.teamSize;

            const isAllPurchase =
              team.participants.filter(
                (participant: DynamicObjectInterface) =>
                  participant?.purchase?.length > 0 && participant.status === 'ACTIVE',
              )?.length >= parseInt(teamSize, 10);

            const isAllWeightIn =
              team.participants.filter(
                (participant: DynamicObjectInterface) =>
                  !!participant?.weightIn?.weightInDate && participant.status === 'ACTIVE',
              )?.length >= parseInt(teamSize, 10);

            const isAllWeightOut =
              team.participants.filter(
                (participant: DynamicObjectInterface) =>
                  !!participant.weightOut?.weightOutDate && participant.status === 'ACTIVE',
              )?.length >= parseInt(teamSize, 10);

            const isAllWin =
              team.participants.filter(
                (participant: DynamicObjectInterface) =>
                  !!participant.isIndividualAchieved && participant.status === 'ACTIVE',
              )?.length >= parseInt(teamSize, 10);

            const isAllValidPurchase =
              team.participants.filter(
                (participant: DynamicObjectInterface) =>
                  participant.purchase[0]?.orderStatus === 1 && participant.status === 'ACTIVE',
              )?.length >= parseInt(teamSize, 10);

            // Find the participant with the matching userId
            const matchingParticipant = team.participants.find(
              (participant: DynamicObjectInterface) => participant.userId === decodedData.userId,
            );

            // Extract the desired data from the matching participant
            const purchase = matchingParticipant?.purchase ?? null;
            const weightIn = matchingParticipant?.weightIn ?? null;
            const weightOut = matchingParticipant?.weightOut ?? null;
            const weightOutDiffValues = matchingParticipant?.weightOutDiffValues ?? null;
            const isIndividualAchieved = matchingParticipant?.isIndividualAchieved ?? null;

            return {
              ...team,
              userName: decodedData?.name,
              name: team.teamName,
              coachId: team.coachId,
              coachName: team.coachName,
              teamStartDate: team.teamStartDate,
              teamEndDate: team.teamEndDate,
              teamSize: teamSize,
              teamId: team.teamId,
              teamStatus: team.status,
              teamPoint: totalPoints,
              teamCreatedDate: teamDetails?.data?.data?.createdDate,
              isAllPurchase: isAllPurchase,
              isAllWeightIn: isAllWeightIn,
              isAllWeightOut: isAllWeightOut,
              isAllWin: isAllWin,
              isAllValidPurchase: isAllValidPurchase,
              isGroupAchieved: isGroupAchieved,
              campaignId: campaignData?.[index]?.campaignId,
              isCoachEnrollment: campaignData?.[index]?.enrollment.some(
                (i: DynamicObjectInterface) => i.enrollmentType === 'COACH' || i.enrollmentType === 'LEADER',
              ),
              purchase,
              weightIn,
              weightOut,
              weightOutDiffValues,
              isIndividualAchieved,
            };
          }),
        );
        setData(dataWithDetails);
      }
    })();
  }, [campaignData, decodedData, index]);

  const handleViewAllClick = () => {
    navigate(`/campaigns/${campaignData[0].campaignId}/teams`);
  };

  const participantValidity = campaignData?.[index]?.configJson?.activities?.participant?.validity?.value
    ? parseInt(campaignData?.[index]?.configJson?.activities?.participant?.validity?.value)
    : null;
  const postAssessmentReminderDays = campaignData?.[index]?.configJson?.postAssessmentReminderDays
    ? campaignData?.[index]?.configJson?.postAssessmentReminderDays
    : null;
  const teamSize = campaignData?.[index]?.configJson?.activities?.participant?.teamSize
    ? parseInt(campaignData?.[index]?.configJson?.activities?.participant?.teamSize)
    : 0;
  const teamLeaderReminderDays = campaignData?.[index]?.configJson?.activities?.coach?.teamLeaderReminderDays;
  const registrationEnd = campaignData?.[index]?.configJson?.registerEnd;
  const isDismiss =
    localStorage.getItem('dismissIncompleteTeamReminder') !== null
      ? JSON.parse(localStorage.getItem('dismissIncompleteTeamReminder')!)
      : null;

  const handleRedirect = () => {
    navigate(`/campaigns/${campaignData?.[index]?.campaignId}`);
  };

  const handleDismiss = () => {
    localStorage.setItem(
      'dismissIncompleteTeamReminder',
      JSON.stringify({
        campaignId: campaignData?.[index]?.campaignId,
        teamId: data?.[0]?.teamId,
      }),
    );
    setShowTeamLeaderReminder(false);
  };

  return (
    <div className={styles['homepage-container']}>
      {showWelcomeBanner && <WelcomeBanner username={decodedData?.name} />}

      {data?.[0]?.enrollmentType === 'COACH' &&
      isDismiss?.campaignId !== campaignData?.[index]?.campaignId &&
      isDismiss?.teamId !== data?.[0]?.teamId &&
      data?.[0]?.participants?.length < teamSize &&
      showTeamLeaderReminder &&
      teamLeaderReminderDays &&
      moment.utc(registrationEnd).isAfter(moment.utc()) &&
      moment
        .utc(data?.[0]?.teamCreatedDate)
        .utcOffset(8 * 60)
        .add(teamLeaderReminderDays, 'days')
        .startOf('day')
        .isSameOrBefore(
          moment()
            .utcOffset(8 * 60)
            .startOf('day'),
          'day',
        ) ? (
        <div className={`${styles['dashboard-container-acknowledgement']}`}>
          <div className={styles['details-card-wrapper']}>
            <div className={styles['details-container']}>
              <div className={`${styles['details-info']} ${styles['details-info-text']}`}>
                <img src={Error} alt={`beforeImg`} width={'32px'} height={'32px'} className={styles['warning']} />
                {t('dashboard.home.message.incompleteTeamReminder')}
              </div>
              <div className={styles['confirm-button']}>
                <Button
                  title={t('dashboard.home.button.dismiss')}
                  type={ButtonTypes.BUTTON}
                  size={ButtonSize.SMALL}
                  buttonStyle={ButtonStyles.OUTLINE}
                  onClick={() => handleDismiss()}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        data?.[0]?.teamStartDate &&
        !!data?.[0]?.weightIn?.weightInDate &&
        !data?.[0]?.weightOut?.weightOutDate &&
        participantValidity &&
        postAssessmentReminderDays &&
        new Date(
          new Date(data?.[0]?.teamStartDate).getTime() +
            (participantValidity - postAssessmentReminderDays) * 24 * 60 * 60 * 1000,
        ).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0) && (
          <div className={`${styles['dashboard-container-acknowledgement']}`}>
            <div className={styles['details-card-wrapper']}>
              <div className={styles['details-container']}>
                <div className={`${styles['details-info']} ${styles['details-info-text']}`}>
                  <img src={Error} alt={`beforeImg`} width={'32px'} height={'32px'} className={styles['warning']} />
                  {t('dashboard.home.message.completePostAssessmentReminder').replace(
                    '{end-date}',
                    formatDateDMYYYY(
                      new Date(
                        new Date(data?.[0]?.teamStartDate).getTime() + participantValidity * 24 * 60 * 60 * 1000,
                      ),
                    ),
                  )}
                </div>
                <div className={styles['confirm-button']}>
                  <Button
                    title={t('dashboard.home.button.updateNow')}
                    type={ButtonTypes.BUTTON}
                    size={ButtonSize.SMALL}
                    buttonStyle={ButtonStyles.OUTLINE}
                    onClick={() => handleRedirect()}
                  />
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {content && (
        <>
          <div className={styles['banner']}>
            <img
              src={isDesktopView ? content?.banner_image?.url : content?.banner_image_mobile?.url}
              alt={t('dashboard.home.label.bannerImageAlt')}
            />
          </div>
        </>
      )}
      {data?.length > 0 && (
        <div className={styles['content']}>
          <div className={styles['title']}>{campaignData[0]?.campaignName}</div>
          <div className={styles['progress-step-container']}>
            {data.slice(0, 4).map((details: DynamicObjectInterface, index) => (
              <div className={styles['progress-step-card']} key={index}>
                <ProgressStep
                  {...details}
                  handleRedirect={
                    details?.participationType === 'ind'
                      ? () => navigate(`/campaigns/${details?.campaignId}`)
                      : details.enrollmentType === 'PARTICIPANT'
                        ? () => navigate(`/campaigns/${details?.campaignId}/myTeam`)
                        : () => navigate(`/campaigns/${details?.campaignId}/teams/${details?.teamId}`)
                  }
                />
                <div className={styles['progress-button-container']}>
                  <Button
                    className={styles['progress-button']}
                    theme={Theme.GREEN}
                    size={ButtonSize.SMALL}
                    buttonStyle={ButtonStyles.OUTLINE}
                    onClick={() => navigate(`/campaigns/${details?.campaignId}`)}
                    title={t('dashboard.home.button.myProgress')}
                  />
                  {details?.participationType !== 'ind' && (
                    <Button
                      className={styles['progress-button']}
                      theme={Theme.GREEN}
                      size={ButtonSize.SMALL}
                      buttonStyle={ButtonStyles.OUTLINE}
                      onClick={() => navigate(`/campaigns/${details?.campaignId}/myTeam`)}
                      title={t('dashboard.home.button.myTeam')}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
          {campaignData[0]?.enrollment?.some(
            (i: DynamicObjectInterface) => i.enrollmentType === 'COACH' || i.enrollmentType === 'LEADER',
          ) &&
            data?.[0]?.totalRecords > 3 && (
              <Button
                size={ButtonSize.MEDIUM}
                buttonWidth={ButtonWidth.FULL_WIDTH}
                buttonStyle={ButtonStyles.OUTLINE}
                onClick={handleViewAllClick}
                className={styles['view-all-button']}
                title={t('dashboard.home.button.viewAll')}
              />
            )}
        </div>
      )}
      {content && (
        <>
          <div className={styles['news-container']}>
            <div className={styles['title']}>{t('dashboard.home.label.newsAndAnnouncements')}</div>
            <div className={styles['news-container-list']}>
              {content?.news_announcements?.map((contents, index) => (
                <div key={index} className={styles['news-card-wrapper']}>
                  <NewsCard data={contents} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      {!loadingArticle && article?.length > 0 && (
        <ArticleCarousel logoUrl={content?.amway_now_logo?.url} cards={article} isMobile={!isDesktopView} />
      )}
    </div>
  );
};
